// client/src/pages/Reports.tsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Globe, Activity, AlertTriangle, BrainCircuit, FileCheck, Files, Wrench, Database, Snowflake, Timer } from 'lucide-react';
import { usePermissions } from '../contexts/PermissionsContext';
import type { PermissionKey } from '../types/permissions';

const REPORTS: {
  key: string;
  permissionKey: PermissionKey;
  path: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  accent: string;
}[] = [
  {
    key: 'multistate',
    permissionKey: 'report.multistate',
    path: '/reports/multistate',
    icon: <Globe size={22} />,
    title: 'MultiState & Consolidated Returns',
    description: 'All returns flagged as multi-state or part of a consolidated filing, grouped by phase.',
    accent: '#64d2ff',
  },
  {
    key: 'logicapps',
    permissionKey: 'report.logicapps',
    path: '/reports/logicapps',
    icon: <Activity size={22} />,
    title: 'Logic App Health',
    description: 'Run history, failure rates, and status for all automation logic apps powering the filing pipeline.',
    accent: '#7aa2ff',
  },
  {
    key: 'operationsConsole',
    permissionKey: 'report.peHealth',
    path: '/reports/operations',
    icon: <Timer size={22} />,
    title: 'Operations',
    description: 'Every scheduled refresh: what it writes, what triggers it, its rails, cadence and run history. Turn jobs on or off, change a schedule, run one now, and see every change made. Failures go to the dev inbox.',
    accent: '#5b6ee1',
  },
  {
    key: 'integrationConsole',
    permissionKey: 'report.peHealth',
    path: '/reports/integration',
    icon: <Database size={22} />,
    title: 'Integration Console',
    description: 'Vendor tables replicated into our own database — what is mirrored, how fresh each one is, and whether an independent audit says the two sides actually agree. Configure cadence and method, run a sync or audit on demand, and review the in-sync scorecard over a measurement window.',
    accent: '#199e70',
  },
  {
    key: 'peHealth',
    permissionKey: 'report.peHealth',
    path: '/reports/pe-health',
    icon: <Database size={22} />,
    title: 'PE Health',
    description: 'Practice Engine database health over time — worker and CPU trend against the throttling ceiling, the queries burning the most CPU, wait growth, orphaned sessions, and vendor-side config changes.',
    accent: '#3987e5',
  },
  {
    key: 'snowflakeCost',
    permissionKey: 'report.snowflakeCost',
    path: '/reports/snowflake-cost',
    icon: <Snowflake size={22} />,
    title: 'Snowflake Cost',
    description: 'Where our Snowflake credits go — billed versus idle credits per warehouse, the queries and users driving spend, and whether auto-suspend can actually recover any of it.',
    accent: '#1d9a8f',
  },
  {
    key: 'errorResolution',
    permissionKey: 'report.errorResolution',
    path: '/reports/error-resolution',
    icon: <Wrench size={22} />,
    title: 'Error Resolution Agent',
    description: 'AI-generated resolution briefs for failing apps and logic apps — what happened, likely cause, and concrete fixes, with a daily digest.',
    accent: '#ff6b6b',
  },
  {
    key: 'badcchcodes',
    permissionKey: 'report.badcchcodes',
    path: '/reports/bad-cch-codes',
    icon: <AlertTriangle size={22} />,
    title: 'Missing CCH Subcodes',
    description: 'Clients added to the system without a valid subcode — each client should have both a client ID and a subcode.',
    accent: '#fbbf24',
  },
  // Tax Return Interactive Reporting disabled — data call removed from AppShellLayout
  // {
  //   key: 'returnAnalytics',
  //   permissionKey: 'report.returnAnalytics',
  //   path: '/reports/return-analytics',
  //   icon: <BrainCircuit size={22} />,
  //   title: 'Tax Return Interactive Reporting',
  //   description: 'Ask natural language questions about return data — phase breakdowns, partner workloads, budget variance, and more — powered by AI.',
  //   accent: '#57d38c',
  // },
  {
    key: 'mfiles2peRecon',
    permissionKey: 'report.mfiles2peRecon',
    path: '/reports/mfiles2pe-recon',
    icon: <FileCheck size={22} />,
    title: 'M-Files to PE Reconciliation',
    description: 'Master audit reconciliation between M-Files and Practice Engine — review and mark items as resolved.',
    accent: '#f472b6',
  },
  {
    key: 'knuula2peRecon',
    permissionKey: 'report.knuula2peRecon',
    path: '/reports/knuula2pe-recon',
    icon: <FileCheck size={22} />,
    title: 'Knuula to PE Reconciliation',
    description: 'Master audit reconciliation between Knuula and Practice Engine — review and mark items as resolved.',
    accent: '#a78bfa',
  },
  {
    key: 'completed1040PaperSrc',
    permissionKey: 'report.completed1040PaperSrc',
    path: '/reports/1040-paper-source-docs',
    icon: <Files size={22} />,
    title: 'Completed 1040s w/ Paper Source Docs',
    description: 'Completed 1040 returns flagged with PAPER source documents — review and clear each row as it is handled.',
    accent: '#fb923c',
  },
];

export default function Reports() {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const visible = REPORTS.filter(r => hasPermission(r.permissionKey));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div>
        <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-.01em' }}>Reports</div>
        <div className="muted" style={{ fontSize: 13, marginTop: 3 }}>Select a report to view</div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
        {visible.map(r => (
          <div
            key={r.key}
            className="card"
            onClick={() => navigate(r.path)}
            style={{
              padding: '20px 22px',
              cursor: 'pointer',
              borderLeft: `3px solid ${r.accent}`,
              transition: 'background .12s, border-color .12s',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = `${r.accent}10`)}
            onMouseLeave={e => (e.currentTarget.style.background = '')}
          >
            <div style={{ color: r.accent, marginBottom: 12 }}>{r.icon}</div>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>{r.title}</div>
            <div className="muted" style={{ fontSize: 13, lineHeight: 1.5 }}>{r.description}</div>
          </div>
        ))}
        {visible.length === 0 && (
          <div className="muted" style={{ fontSize: 13 }}>No reports available.</div>
        )}
      </div>
    </div>
  );
}
